import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/* ======================= Helpers ======================= */

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const fractionToLatex = ({ numerator, denominator }) => {
  if (!denominator || denominator === 0) return "";
  if (denominator === 1) return `${numerator}`;
  return `\\dfrac{${numerator}}{${denominator}}`;
};

const simplifyFraction = (num, den) => {
  const gcd = (a, b) => {
    a = Math.abs(a);
    b = Math.abs(b);
    if (a === 0 && b === 0) return 1;
    while (b !== 0) {
      const t = b;
      b = a % b;
      a = t;
    }
    return a === 0 ? 1 : a;
  };

  const g = gcd(num, den);
  return { numerator: num / g, denominator: den / g };
};

// ułamek zwykły / mieszany (bez kropki/przecinka)
const parseFractionFromString = (input) => {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  // jeśli jest kropka/przecinek – traktujemy to jako liczbę dziesiętną, nie ułamek
  if (trimmed.includes(".") || trimmed.includes(",")) return null;

  let sign = 1;
  let rest = trimmed;
  if (rest[0] === "-" || rest[0] === "+") {
    if (rest[0] === "-") sign = -1;
    rest = rest.slice(1).trim();
  }

  let whole = 0;
  let num;
  let den;

  const spaceParts = rest.split(/\s+/);

  if (spaceParts.length === 2) {
    // zapis mieszany: "2 3/5"
    whole = parseInt(spaceParts[0], 10);
    if (!Number.isFinite(whole)) return null;

    const fracParts = spaceParts[1].split("/");
    if (fracParts.length !== 2) return null;

    num = parseInt(fracParts[0], 10);
    den = parseInt(fracParts[1], 10);
    if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;

    num = sign * (whole * den + num);
  } else {
    // "3/5" lub "4"
    const fracParts = rest.split("/");
    if (fracParts.length === 1) {
      num = sign * parseInt(fracParts[0], 10);
      den = 1;
      if (!Number.isFinite(num)) return null;
    } else if (fracParts.length === 2) {
      num = sign * parseInt(fracParts[0], 10);
      den = parseInt(fracParts[1], 10);
      if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
    } else return null;
  }

  return simplifyFraction(num, den);
};

// liczba dziesiętna (kropka lub przecinek)
const parseDecimalFromString = (input) => {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(",", ".");
  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;

  return value;
};

// format do max 2 miejsc po przecinku, bez nieznaczących zer (np. 1 → "1", 1.2 → "1.2", 0.75 → "0.75")
const formatMax2 = (value) => {
  let s = value.toFixed(2);
  s = s.replace(/\.?0+$/, "");
  return s;
};

// format "surowego" iloczynu – do 4 miejsc po przecinku, bez końcowych zer
const formatRawProduct = (value) => {
  let s = value.toFixed(4);
  s = s.replace(/0+$/, "");
  if (s.endsWith(".")) s = s.slice(0, -1);
  return s;
};

/* ======================= GENERATOR ======================= */

const VARIANTS = ["++", "--", "+-", "-+"];

export function generateTask(variant) {
  let pattern = variant;
  if (!pattern || !VARIANTS.includes(pattern)) {
    pattern = VARIANTS[randInt(0, VARIANTS.length - 1)];
  }

  const isIncrease1 = pattern[0] === "+";
  const isIncrease2 = pattern[1] === "+";

  const percents = [5, 10, 15, 20, 25, 30, 35, 40];
  const percent1 = percents[randInt(0, percents.length - 1)];
  const percent2 = percents[randInt(0, percents.length - 1)];

  const base = 10 * randInt(5, 25); // np. 50, 60, ..., 250 zł

  const factor1 = 1 + (isIncrease1 ? percent1 : -percent1) / 100;
  const factor2 = 1 + (isIncrease2 ? percent2 : -percent2) / 100;

  const raw = base * factor1 * factor2; // niezaokrąglony iloczyn
  const result = Number(raw.toFixed(2)); // do groszy

  return {
    type: "PercentChangeDoubleTask",
    data: {
      base,
      percent1,
      percent2,
      isIncrease1,
      isIncrease2,
      factor1,
      factor2,
      raw,
      result,
    },
  };
}

/* ======================= RENDER TASK ======================= */

export function renderTask({ data }) {
  const { base, percent1, percent2, isIncrease1, isIncrease2 } = data;

  const verb1 = isIncrease1 ? "podniesiono" : "obniżono";
  const verb2 = isIncrease2 ? "podniesiono" : "obniżono";

  return (
    <p>
      Cenę pewnego towaru równą{" "}
      <InlineMath math={`${base}\\,\\text{zł}`} /> najpierw {verb1} o{" "}
      <InlineMath math={`${percent1}\\%`} />, a następnie {verb2} o{" "}
      <InlineMath math={`${percent2}\\%`} />. Oblicz nową cenę tego towaru.
    </p>
  );
}

/* ======================= INPUT ======================= */

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div style={{ marginTop: "0.75rem" }}>
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        placeholder="np. 151.88 albo 151 22/25"
        label=""
        suffixLatex="\text{zł}"
      />
    </div>
  );
}

/* ======================= CHECK ======================= */

export function checkAnswer({ data }, userAnswer) {
  const expected = data.result;

  if (!userAnswer || !userAnswer.raw) {
    return { isCorrect: false, expected, actual: null, comment: null };
  }

  const raw = String(userAnswer.raw).trim();

  // 1) liczba dziesiętna (kropka/przecinek)
  let value = parseDecimalFromString(raw);

  // 2) jeżeli nie wyszło – spróbuj jako ułamek zwykły / mieszany
  if (value === null) {
    const frac = parseFractionFromString(raw);
    if (frac) {
      value = frac.numerator / frac.denominator;
    }
  }

  if (value === null) {
    return { isCorrect: false, expected, actual: null, comment: null };
  }

  // dokładność do 1 grosza
  const isCorrect = Math.abs(value - expected) <= 0.01;

  return { isCorrect, expected, actual: value, comment: null };
}

/* ======================= ANSWER & USER ANSWER ======================= */

export function renderAnswer({ data }) {
  const text = formatMax2(data.result);
  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <InlineMath math={`${text}\\,\\text{zł}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || !userAnswer.raw || !String(userAnswer.raw).trim()) {
    return <i>(brak odpowiedzi)</i>;
  }
  const norm = String(userAnswer.raw).trim().replace(",", ".");
  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <InlineMath math={`${norm}\\,\\text{zł}`} />
    </div>
  );
}

/* ======================= EXPLANATION ======================= */

export function renderExplanation({ data }) {
  const {
    base,
    percent1,
    percent2,
    isIncrease1,
    isIncrease2,
    factor1,
    factor2,
    raw,
    result,
  } = data;

  const sign1 = isIncrease1 ? "+" : "-";
  const sign2 = isIncrease2 ? "+" : "-";

  const f1 = formatMax2(factor1);
  const f2 = formatMax2(factor2);
  const rawStr = formatRawProduct(raw);
  const resStr = formatMax2(result);

  const step1a = `a_1 = 1 ${sign1} \\dfrac{${percent1}}{100} = ${f1}`;
  const step1b = `a_2 = 1 ${sign2} \\dfrac{${percent2}}{100} = ${f2}`;

  const step2 = `K = ${base} \\cdot a_1 \\cdot a_2`;
  const step3 = `K = ${base} \\cdot ${f1} \\cdot ${f2} = ${rawStr} \\approx ${resStr}\\,\\text{zł}`;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginTop: 12,
      }}
    >
      <p>
        <strong>Krok 1:</strong> Zapisz współczynniki zmiany ceny dla każdej
        operacji.
      </p>
      <BlockMath math={step1a} />
      <BlockMath math={step1b} />

      <p>
        <strong>Krok 2:</strong> Zapisz wzór na nową cenę po dwóch zmianach.
      </p>
      <BlockMath math={step2} />

      <p>
        <strong>Krok 3:</strong> Podstaw i oblicz – najpierw dokładny iloczyn,
        a potem zaokrąglenie do groszy.
      </p>
      <BlockMath math={step3} />
    </div>
  );
}
